import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location]);

    // Updated Navigation: 'Services' -> 'Expertise', removed 'Products'
    const navLinks = [
        { name: "Home", path: "/" },
        { name: "Expertise", path: "/services" },
        { name: "Products", path: "/products" },
        { name: "Careers", path: "/careers" },
        { name: "Contact", path: "/contact" },
    ];

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                ? "bg-white/90 backdrop-blur-md shadow-sm py-3 border-b border-gray-100"
                : "bg-transparent py-5"
                }`}
        >
            <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 group">
                    <img
                        src="/images/smartreceptors_logo.png"
                        alt="SmartReceptors"
                        className="h-8 object-contain"
                    />
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {navLinks.map((link) => (
                        <Link
                            key={link.name}
                            to={link.path}
                            className={`text-sm font-medium transition-all duration-300 relative ${location.pathname === link.path
                                ? "text-primary-600 font-semibold"
                                : "text-gray-600 hover:text-primary-600"
                                }`}
                        >
                            {link.name}
                            {location.pathname === link.path && (
                                <motion.div
                                    layoutId="navbar-indicator"
                                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-600"
                                    initial={false}
                                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                />
                            )}
                        </Link>
                    ))}
                    <a
                        href="https://www.linkedin.com/company/smartreceptors/"
                        target="_blank"
                        rel="noreferrer"
                        className="px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
                    >
                        LinkedIn
                    </a>
                </div>

                <button
                    className="md:hidden text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {isOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-white shadow-xl overflow-hidden border-t border-gray-100"
                    >
                        <div className="flex flex-col p-6 gap-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    className={`flex items-center justify-between text-base py-3 px-4 rounded-xl transition-colors ${location.pathname === link.path
                                        ? "text-primary-600 font-semibold bg-primary-50"
                                        : "text-gray-700 hover:bg-gray-50"
                                        }`}
                                >
                                    {link.name}
                                    <ChevronRight size={16} />
                                </Link>
                            ))}
                            <hr className="border-gray-100 my-2" />
                            <a
                                href="https://www.linkedin.com/company/smartreceptors/"
                                target="_blank"
                                rel="noreferrer"
                                className="text-center w-full py-3.5 bg-slate-900 text-white rounded-xl font-medium shadow-lg shadow-slate-900/20"
                            >
                                Follow on LinkedIn
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
